import moment from 'moment';

/**
 * Detects if a date string is ISO format (UTC)
 * @param {string} str - date string to check
 * @returns {boolean}
 */
function isISOString(str) {
  if (!str || typeof str !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(str);
}

/**
 * Converts a date to the user's local timezone for display.
 * Handles both ISO strings (UTC) and legacy toString() formats.
 *
 * @param {string} dateString - ISO date string or any parseable date
 * @param {string} format - moment format string (default: 'DD MMM, HH:mm')
 * @returns {string} formatted date in user's local timezone
 */
export function formatToLocalTimezone(dateString, format = 'DD MMM, HH:mm') {
  if (!dateString) return '';

  if (isISOString(dateString)) {
    // ISO string - parse as UTC and convert to local
    return moment.utc(dateString).local().format(format);
  }

  // Legacy format or other - moment will try to parse it
  return moment(dateString).local().format(format);
}

/**
 * Formats a date with time and year, converting to local timezone.
 * Shows year only if different from current year.
 *
 * @param {string} dateString - date string to format
 * @returns {string} formatted date
 */
export function formatToLocalWithTimeAndYear(dateString) {
  if (!dateString) return '';

  const localMoment = isISOString(dateString)
    ? moment.utc(dateString).local()
    : moment(dateString).local();

  if (localMoment.year() === moment().year()) {
    return localMoment.format('DD MMM, HH:mm');
  }
  return localMoment.format('DD MMM YYYY, HH:mm');
}

/**
 * Formats a TestCard date.
 * The date/time in TestCard is stored in the user's selected timezone.
 * Since we're displaying to the same user, just format it as-is.
 *
 * @param {string} startDate - date in YYYY-MM-DD format
 * @param {string} startTime - time in HH:mm format
 * @param {string} timezone - timezone (ignored for now - display as stored)
 * @param {string} format - moment format string
 * @returns {string} formatted date
 */
// eslint-disable-next-line no-unused-vars
export function formatTestCardDate(startDate, startTime, timezone, format = 'DD MMM, HH:mm') {
  if (!startDate) return '';

  const dateTimeString = `${startDate} ${startTime || '00:00'}`;

  // The TestCard stores the date/time as the user entered it (in their timezone)
  // Just display it as-is without conversion
  return moment(dateTimeString, 'YYYY-MM-DD HH:mm').format(format);
}

/**
 * Formats a TestCard date with year support.
 *
 * @param {string} startDate - date in YYYY-MM-DD format
 * @param {string} startTime - time in HH:mm format
 * @param {string} timezone - timezone (ignored for now)
 * @returns {string} formatted date
 */
export function formatTestCardDateWithYear(startDate, startTime, timezone) {
  if (!startDate) return '';

  const dateTimeString = `${startDate} ${startTime || '00:00'}`;
  // Scans scheduled with a timezone store their start as wall-clock in that
  // timezone, so display it as-is. Immediate/server-set scans (no timezone)
  // store it in server (UTC) time, so convert to the user's local time.
  const localMoment = timezone
    ? moment(dateTimeString, 'YYYY-MM-DD HH:mm')
    : moment.utc(dateTimeString, 'YYYY-MM-DD HH:mm').local();

  if (localMoment.year() === moment().year()) {
    return localMoment.format('DD MMM, HH:mm');
  }
  return localMoment.format('DD MMM YYYY, HH:mm');
}

/**
 * Calculates duration between two dates with proper timezone handling.
 *
 * @param {string} endDate - end date string
 * @param {string} startDate - start date string
 * @returns {number} difference in minutes
 */
export function getDurationInMinutes(endDate, startDate) {
  if (!endDate || !startDate) return 0;

  const endMoment = isISOString(endDate) ? moment.utc(endDate) : moment(endDate);
  const startMoment = isISOString(startDate) ? moment.utc(startDate) : moment(startDate);

  return endMoment.diff(startMoment, 'minutes');
}

/**
 * Formats duration in minutes to a human-readable string.
 *
 * @param {number} diffInMinutes - duration in minutes
 * @returns {string} formatted duration (e.g., "02h 15m" or "45m")
 */
export function formatDuration(diffInMinutes) {
  if (!diffInMinutes || diffInMinutes <= 0) return '0m';

  const pad = n => (n > 9 ? n : `0${n}`);

  if (diffInMinutes >= 60) {
    const hours = Math.floor(diffInMinutes / 60);
    const remainMinutes = diffInMinutes % 60;

    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remainHours = hours % 24;
      return `${pad(days)}d ${pad(remainHours)}h ${pad(remainMinutes)}m`;
    }
    return `${pad(hours)}h ${pad(remainMinutes)}m`;
  }
  return `${pad(diffInMinutes)}m`;
}

/**
 * Calculates and formats duration between two dates.
 *
 * @param {string} endDate - end date string
 * @param {string} startDate - start date string
 * @returns {string} formatted duration
 */
export function getFormattedDuration(endDate, startDate) {
  const minutes = getDurationInMinutes(endDate, startDate);
  return formatDuration(minutes);
}

/**
 * Gets the current user's timezone offset in minutes.
 * Use this when creating new scans to send to the backend.
 *
 * @returns {number} offset in minutes (e.g., 120 for UTC+2)
 */
export function getCurrentTimezoneOffset() {
  return moment().utcOffset();
}
